import { Element } from './element';
import type { MnaMatrix } from './mna';
import { fmt } from './format';

/** 휴대폰 부품별 대략적인 소비 전력 (W) */
export const LOAD_PRESETS: Record<string, { label: string; watts: number }> = {
  screen: { label: '화면 (밝기 중간)', watts: 1.2 },
  'screen-max': { label: '화면 (최대 밝기)', watts: 2.5 },
  'cpu-idle': { label: 'CPU 대기', watts: 0.15 },
  'cpu-max': { label: 'CPU 풀로드 (게임)', watts: 3.5 },
  radio: { label: '통신 (LTE/5G)', watts: 1.0 },
  camera: { label: '카메라', watts: 1.8 },
  speaker: { label: '스피커', watts: 0.5 },
  custom: { label: '직접 입력', watts: 1 },
};

/**
 * 정전력 부하 — 전압이 달라져도 같은 전력을 쓴다 (I = P / V).
 * 실제 전자기기(휴대폰 칩, 화면)는 안에 레귤레이터가 있어서 이렇게 행동한다.
 * 전압이 vmin 밑으로 떨어지면 꺼진다 (브라운아웃). pin0 = +, pin1 = −
 */
export class ConstantPowerLoad extends Element {
  private iIter = 0;
  private on = true;

  override get nonlinear(): boolean {
    return true;
  }
  override resetState(): void {
    this.iIter = 0;
    this.on = false; // 첫 스텝에서 전압을 확인한 뒤 켜진다
  }

  get watts(): number {
    if (!this.bool('enabled', true)) return 0;
    const preset = LOAD_PRESETS[this.str('preset', 'custom')];
    if (!preset || this.str('preset', 'custom') === 'custom') return Math.max(this.num('power', 1), 0);
    return preset.watts;
  }
  private vmin(): number {
    return Math.max(this.num('vmin', 2.8), 0.1);
  }
  get running(): boolean {
    return this.on && this.watts > 0;
  }

  override stamp(m: MnaMatrix): void {
    const [a, b] = this.nodes;
    m.conductance(a, b, 1e-9);
    if (this.iIter !== 0) m.currentSource(a, b, this.iIter);
  }

  override updateNewton(m: MnaMatrix): number {
    const v = m.nodeVoltage(this.nodes[0]) - m.nodeVoltage(this.nodes[1]);
    // 반복 중 전압이 크게 무너지면(전원이 끊김) 바로 끈다
    if (this.on && v < this.vmin() * 0.5) this.on = false;
    const next = this.running ? this.watts / Math.max(v, this.vmin() * 0.9) : 0;
    const d = Math.abs(next - this.iIter);
    this.iIter = next;
    return d;
  }

  protected override computeCurrent(): number {
    return this.iIter + this.voltageDrop * 1e-9;
  }

  override endStep(m: MnaMatrix, dt: number): void {
    super.endStep(m, dt);
    const v = this.voltageDrop;
    const was = this.on;
    this.on = v >= (was ? this.vmin() : this.vmin() + 0.1);
    if (!this.on) this.iIter = 0;
  }

  info(): string[] {
    const preset = LOAD_PRESETS[this.str('preset', 'custom')];
    return [
      '정전력 부하',
      `${preset?.label ?? '직접 입력'} · ${fmt(this.watts, 'W')}`,
      `상태 ${!this.bool('enabled', true) ? '꺼짐' : this.on ? '동작' : '전압 부족으로 꺼짐'}`,
      `전압 ${fmt(this.voltageDrop, 'V')}`,
      `전류 ${fmt(this.current, 'A')}`,
      `실제 전력 ${fmt(this.power, 'W')}`,
    ];
  }
}
